import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter, RefreshCw, ClipboardList, CheckCircle, Clock, BarChart3, FolderOpen } from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { t, translateStatus } from '@/i18n/ro';
import { useAuth } from '@/context/AuthContext';
import { getMyWorkPackages } from '@/services/workpackages.service';
import { getMyDossierTasks } from '@/services/dossier.service';
import {
  Button,
  Select,
  Card,
  LoadingState,
  EmptyState,
} from '@/components/common';
import ActivityDetailModal from '@/components/dossier/ActivityDetailModal';
import type { DossierTask } from '@/types';

// Culori pentru grafice
const CHART_COLORS = {
  todo: '#F59E0B',
  inProgress: '#3B82F6',
  completed: '#10B981',
};

// Grupele de status folosite în „Activitățile mele" (responsabilități din dosare)
type StatusGroupKey = 'todo' | 'inProgress' | 'completed';

// Normalizează statusul eterogen al elementelor de dosar în cele 3 grupe.
// completed/approved/finalizat/închis → Finalizat; in_progress/curs/lucru → În lucru; restul → De făcut.
function normalizeDossierStatus(status?: string): StatusGroupKey {
  const s = (status || '').toLowerCase();
  if (/complet|approv|finaliz|închis|inchis|closed|done/.test(s)) return 'completed';
  if (/progres|curs|lucru|desfasur|desfășur|ongoing/.test(s)) return 'inProgress';
  return 'todo';
}

// Metadate pe tip de element (badge)
interface DossierTaskTypeMeta {
  label: string;
  badgeClass: string;
}

const DOSSIER_TASK_TYPES: Record<DossierTask['source_type'], DossierTaskTypeMeta> = {
  dossier_activity: { label: 'Activitate', badgeClass: 'bg-indigo-100 text-indigo-700' },
  procurement: { label: 'Achiziție', badgeClass: 'bg-purple-100 text-purple-700' },
  procurement_activity: { label: 'Activitate achiziție', badgeClass: 'bg-teal-100 text-teal-700' },
  deliverable: { label: 'Livrabil', badgeClass: 'bg-blue-100 text-blue-700' },
  implementation_event: { label: 'Eveniment', badgeClass: 'bg-amber-100 text-amber-700' },
};

export function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [dossierTasks, setDossierTasks] = useState<DossierTask[]>([]);
  const [workPackageCount, setWorkPackageCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filtre
  const [selectedProject, setSelectedProject] = useState<number | ''>('');

  // Activitatea de dosar deschisă în modalul de detalii (null = închis).
  const [activeActivityId, setActiveActivityId] = useState<number | null>(null);

  // Încărcare date
  const loadData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [dossierTasksData, wpData] = await Promise.all([
        getMyDossierTasks(),
        // Contor discret pentru activitățile OpenProject — nu blochează dashboard-ul dacă pică.
        getMyWorkPackages().catch(() => []),
      ]);

      setDossierTasks(dossierTasksData);
      setWorkPackageCount(wpData.length);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError(t('errors.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Aplicare filtru de proiect
  const filteredTasks = useMemo(() => {
    if (!selectedProject) return dossierTasks;
    return dossierTasks.filter((task) => task.project_id === selectedProject);
  }, [dossierTasks, selectedProject]);

  // Grupare după status normalizat
  const groupedTasks = useMemo(() => {
    const groups: Record<StatusGroupKey, DossierTask[]> = {
      todo: [],
      inProgress: [],
      completed: [],
    };
    filteredTasks.forEach((task) => {
      groups[normalizeDossierStatus(task.status)].push(task);
    });
    return groups;
  }, [filteredTasks]);

  // Date pentru graficul pie (distribuție după status)
  const pieChartData = useMemo(() => [
    { name: 'De făcut', value: groupedTasks.todo.length, color: CHART_COLORS.todo },
    { name: 'În lucru', value: groupedTasks.inProgress.length, color: CHART_COLORS.inProgress },
    { name: 'Finalizate', value: groupedTasks.completed.length, color: CHART_COLORS.completed },
  ].filter((d) => d.value > 0), [groupedTasks]);

  // Date pentru graficul bar (activități per proiect, top 5)
  const barChartData = useMemo(() => {
    const projectCounts = new Map<number, { name: string; todo: number; inProgress: number; completed: number }>();

    filteredTasks.forEach((task) => {
      if (!projectCounts.has(task.project_id)) {
        projectCounts.set(task.project_id, {
          name: task.project_name || `Proiect ${task.project_id}`,
          todo: 0,
          inProgress: 0,
          completed: 0,
        });
      }
      const counts = projectCounts.get(task.project_id)!;
      counts[normalizeDossierStatus(task.status)]++;
    });

    return Array.from(projectCounts.values())
      .sort((a, b) => (b.todo + b.inProgress + b.completed) - (a.todo + a.inProgress + a.completed))
      .slice(0, 5);
  }, [filteredTasks]);

  // Opțiuni pentru filtrul de proiect — derivate din dosarele utilizatorului
  const projectOptions = useMemo(() => {
    const seen = new Map<number, string>();
    dossierTasks.forEach((task) => {
      if (!seen.has(task.project_id)) {
        seen.set(task.project_id, task.project_name || `Proiect ${task.project_id}`);
      }
    });
    return [
      { value: '', label: t('dashboard.filters.allProjects') },
      ...Array.from(seen.entries()).map(([id, name]) => ({ value: id, label: name })),
    ];
  }, [dossierTasks]);

  const handleTaskClick = (task: DossierTask) => {
    // Activitățile de dosar deschid modalul de detalii (cu schimbare status inline).
    // Restul tipurilor (achiziții/contracte/livrabile/evenimente) navighează ca înainte.
    if (task.source_type === 'dossier_activity') {
      setActiveActivityId(task.id);
      return;
    }
    navigate(task.link);
  };

  const userName = user
    ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.login
    : '';

  if (isLoading) {
    return <LoadingState message={t('common.loading')} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('dashboard.title')}
          </h1>
          <p className="text-gray-600 mt-1">
            {t('dashboard.welcome')}, {userName}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {workPackageCount > 0 && (
            <span className="text-sm text-gray-500">
              +{workPackageCount} {workPackageCount === 1 ? 'activitate' : 'activități'} OpenProject
            </span>
          )}
          <Button
            variant="secondary"
            leftIcon={<RefreshCw className="w-4 h-4" />}
            onClick={loadData}
          >
            {t('common.refresh')}
          </Button>
        </div>
      </div>

      {/* Filtre */}
      {projectOptions.length > 1 && (
        <Card>
          <div className="flex flex-wrap items-center gap-4">
            <Filter className="w-5 h-5 text-gray-400" />
            <div className="flex-1 min-w-[200px] max-w-[280px]">
              <Select
                options={projectOptions}
                value={selectedProject}
                onChange={(e) =>
                  setSelectedProject(e.target.value ? Number(e.target.value) : '')
                }
                placeholder={t('dashboard.filters.project')}
              />
            </div>
          </div>
        </Card>
      )}

      {/* Error state */}
      {error && (
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {error}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<ClipboardList className="w-6 h-6" />}
          label="Total activități"
          value={filteredTasks.length}
          color="bg-gray-500"
        />
        <StatCard
          icon={<Clock className="w-6 h-6" />}
          label="De făcut"
          value={groupedTasks.todo.length}
          color="bg-amber-500"
        />
        <StatCard
          icon={<BarChart3 className="w-6 h-6" />}
          label="În lucru"
          value={groupedTasks.inProgress.length}
          color="bg-blue-500"
        />
        <StatCard
          icon={<CheckCircle className="w-6 h-6" />}
          label="Finalizate"
          value={groupedTasks.completed.length}
          color="bg-green-500"
        />
      </div>

      {/* Charts */}
      {filteredTasks.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pie Chart - Distribuție după status */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Distribuție după status</h3>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                    outerRadius={100}
                    dataKey="value"
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Bar Chart - Activități per proiect */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Activități per proiect</h3>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barChartData} layout="vertical">
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis
                    dataKey="name"
                    type="category"
                    width={150}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="todo" stackId="a" fill={CHART_COLORS.todo} name="De făcut" />
                  <Bar dataKey="inProgress" stackId="a" fill={CHART_COLORS.inProgress} name="În lucru" />
                  <Bar dataKey="completed" stackId="a" fill={CHART_COLORS.completed} name="Finalizate" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      )}

      {/* Lista grupată a responsabilităților din dosare */}
      {filteredTasks.length === 0 ? (
        <EmptyState
          icon="calendar"
          title={t('dashboard.noActivities')}
          description="Nu aveți responsabilități din dosare atribuite în acest moment."
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <DossierTaskGroup
            title={t('dashboard.groups.todo')}
            tasks={groupedTasks.todo}
            color="bg-amber-500"
            onItemClick={handleTaskClick}
          />
          <DossierTaskGroup
            title={t('dashboard.groups.inProgress')}
            tasks={groupedTasks.inProgress}
            color="bg-blue-500"
            onItemClick={handleTaskClick}
          />
          <DossierTaskGroup
            title={t('dashboard.groups.completed')}
            tasks={groupedTasks.completed}
            color="bg-green-500"
            onItemClick={handleTaskClick}
          />
        </div>
      )}

      {/* Modal detalii activitate (pentru elementele de tip activitate de dosar).
          După schimbarea statusului reîncărcăm lista ca elementul să se mute în grupul corect. */}
      {activeActivityId !== null && (
        <ActivityDetailModal
          activityId={activeActivityId}
          onClose={() => setActiveActivityId(null)}
          onStatusChange={() => loadData()}
        />
      )}
    </div>
  );
}

// Grup de responsabilități din dosare, pe o grupă de status
interface DossierTaskGroupProps {
  title: string;
  tasks: DossierTask[];
  color: string;
  onItemClick: (task: DossierTask) => void;
}

function DossierTaskGroup({ title, tasks, color, onItemClick }: DossierTaskGroupProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${color}`} />
          <h2 className="font-semibold text-gray-900">{title}</h2>
        </div>
        <span className="text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
          {tasks.length}
        </span>
      </div>
      <div className="p-3 space-y-2 max-h-[500px] overflow-y-auto">
        {tasks.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">
            {t('common.noData')}
          </p>
        ) : (
          tasks.map((task) => (
            <DossierTaskCard
              key={`${task.source_type}-${task.id}`}
              task={task}
              onClick={() => onItemClick(task)}
            />
          ))
        )}
      </div>
    </div>
  );
}

// Card pentru o responsabilitate din dosar
interface DossierTaskCardProps {
  task: DossierTask;
  onClick: () => void;
}

function DossierTaskCard({ task, onClick }: DossierTaskCardProps) {
  const meta = DOSSIER_TASK_TYPES[task.source_type];
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left p-3 rounded-lg border border-gray-100 hover:border-gray-300 hover:bg-gray-50 transition-colors"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${meta?.badgeClass ?? 'bg-gray-100 text-gray-700'}`}
        >
          {meta?.label ?? task.source_type}
        </span>
        {task.status && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs text-gray-600 bg-gray-100">
            {translateStatus(task.status)}
          </span>
        )}
      </div>
      <p className="font-medium text-gray-900 mt-2 line-clamp-2">
        {task.name}
      </p>
      <p className="text-sm text-gray-500 mt-1 flex items-center gap-1 truncate">
        <FolderOpen className="w-3.5 h-3.5 shrink-0" />
        <span className="truncate">{task.project_name}</span>
      </p>
    </button>
  );
}

// Card de statistici
interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: string;
}

function StatCard({ icon, label, value, color }: StatCardProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-lg ${color} text-white`}>
          {icon}
        </div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
}
